// Sidebar.jsx
import React, { useContext, useState, useEffect, useMemo } from "react";
import "./Sidebar.css";
import { CaseContext } from "../../Pages/CaseContext";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api";
import { ROLES, CASE_ROLES, isDetectiveSupervisor } from "../../constants/roles";

export const SideBar = ({
  cases: initialCases = [],
  activePage,
  activeTab,
  setActiveTab,
  onShowCaseSelector,
  variant = "default",
  isDS: isDSProp = null,
  showAddCase = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { caseDetails } = location.state || {};
  const { selectedCase, selectedLead, setSelectedLead, setSelectedCase } =
    useContext(CaseContext);

  const LEAD_CRUMB_PAGES = useMemo(
    () => new Set(["LeadReview", "LeadInformation"]),
    []
  );

  const signedInOfficer = localStorage.getItem("loggedInUser");
  const signedInUserId  = localStorage.getItem("userId");
  const signedInRole = selectedCase?.role;
  const systemRole = localStorage.getItem("systemRole") || localStorage.getItem("role");

  const [caseDropdownOpen, setCaseDropdownOpen] = useState(true);
  const [caseList, setCaseList] = useState(initialCases);

  // True if user is a Detective Supervisor either by system role or by case-level role in any case
  const isUserDS = useMemo(
    () =>
      isDetectiveSupervisor(systemRole) ||
      caseList.some((c) => c.role === CASE_ROLES.DETECTIVE_SUPERVISOR),
    [systemRole, caseList]
  );

  // Investigator-only: assigned leads for badge counts
  const [assignedLeadsList, setAssignedLeadsList] = useState([]);

  // CM/DS-only: per-case "In Review" counts for all ongoing cases
  const [reviewCountByCase, setReviewCountByCase] = useState({});

  const folderIcon  = `${process.env.PUBLIC_URL}/Materials/case1.png`;
  const folderIcon1 = `${process.env.PUBLIC_URL}/Materials/case.png`;
  const homeIcon    = `${process.env.PUBLIC_URL}/Materials/home.png`;
  const logIcon     = `${process.env.PUBLIC_URL}/Materials/log2.png`;
  const addIcon     = `${process.env.PUBLIC_URL}/Materials/addicon.svg`;
  const printIcon   = `${process.env.PUBLIC_URL}/Materials/print.png`;
  const bellIcon    = `${process.env.PUBLIC_URL}/Materials/notification.png`;
   const searchIcon    = `${process.env.PUBLIC_URL}/Materials/search1.png`;

  const handleCreateLead = () => {
    navigate("/createlead", {
      state: {
        caseDetails: selectedCase,
        leadOrigin: selectedLead?.leadNo || selectedLead?.id || null,
      },
    });
  };

  const goToCasePage = () => {
    const role = selectedCase?.role;
    const dest =
      role === "Investigator" || role === "Officer"
        ? "/Investigator"
        : role === "Case Manager" || role === "Detective Supervisor"
        ? "/CasePageManager"
        : role === "Read Only"
        ? "/LeadsDesk"
        : "/HomePage";
    navigate(dest, { state: { caseDetails: selectedCase } });
  };

  // Fetch ONGOING cases for this officer
  useEffect(() => {
    const fetchCases = async () => {
      try {
        const token = localStorage.getItem("token");
        const { data } = await api.get("/api/cases/cases-by-officer", {
          headers: { Authorization: `Bearer ${token}` },
          params: { officerName: signedInOfficer },
          suppressGlobalError: true,
        });

        const ongoing = (data || [])
          .filter((c) => c.caseStatus === "ONGOING")
          .map((c) => ({
            _id: c._id,
            caseNo: c.caseNo,
            title: c.caseName,
            role: c.role || "Unknown",
          }));

        setCaseList(ongoing);
      } catch (err) {
        console.error("Error fetching cases", err);
        setCaseList([]);
      }
    };
    fetchCases();
  }, [signedInOfficer]);

  // Investigator: fetch only leads Assigned to this user in ongoing cases
  useEffect(() => {
    if (!caseList.length) {
      setAssignedLeadsList([]);
      return;
    }
    const fetchLeads = async () => {
      try {
        const token = localStorage.getItem("token");
        const { data } = await api.get("/api/lead/assignedTo-leads", {
          headers: { Authorization: `Bearer ${token}` },
          suppressGlobalError: true,
        });

        const caseIds = new Set(caseList.map((c) => String(c._id)));
        const filtered = (data || [])
          .filter(
            (l) =>
              caseIds.has(String(l.caseId)) &&
              l.leadStatus === "Assigned" &&
              l.assignedTo?.some((a) =>
                signedInUserId && a.userId
                  ? String(a.userId) === signedInUserId
                  : a.username === signedInOfficer
              )
          )
          .map((l) => ({
            id: l.leadNo,
            description: l.description,
            caseId: l.caseId,
            caseNo: l.caseNo,
            assignedTo: l.assignedTo,
            leadStatus: l.leadStatus,
          }));

        setAssignedLeadsList(filtered);
      } catch (err) {
        console.error("Error fetching assigned leads", err);
        setAssignedLeadsList([]);
      }
    };
    fetchLeads();
  }, [signedInOfficer, caseList]);

  // Fetch per-case counts based on the officer's role in EACH specific case
  useEffect(() => {
    if (!caseList.length) {
      setReviewCountByCase({});
      return;
    }

    let cancelled = false;

    const fetchAllCounts = async () => {
      const token = localStorage.getItem("token");
      try {
        const entries = await Promise.all(
          caseList.map(async (c) => {
            try {
              const isCMorDS = c.role === "Case Manager" || c.role === "Detective Supervisor";
              const { data } = await api.get(`/api/lead/case/${c._id}`, {
                headers: { Authorization: `Bearer ${token}` },
                suppressGlobalError: true,
              });

              const arr = Array.isArray(data?.leads)
                ? data.leads
                : Array.isArray(data)
                ? data
                : [];

              if (isCMorDS) {
                const count = arr.filter(
                  (l) =>
                    l.leadStatus === "In Review" ||
                    l.leadStatus === "Submitted" ||
                    l.leadStatus === "To review"
                ).length;
                return [String(c._id), count];
              } else {
                const assignedCount = arr.filter(
                  (l) =>
                    l.leadStatus === "Assigned" &&
                    l.assignedTo?.some((a) =>
                      signedInUserId && a.userId
                        ? String(a.userId) === signedInUserId
                        : a.username === signedInOfficer
                    )
                ).length;
                return [String(c._id), assignedCount];
              }
            } catch (e) {
              return [String(c._id), 0];
            }
          })
        );

        if (!cancelled) setReviewCountByCase(Object.fromEntries(entries));
      } catch (e) {
        console.error("Error fetching counts", e);
        if (!cancelled) setReviewCountByCase({});
      }
    };

    fetchAllCounts();
    return () => {
      cancelled = true;
    };
  }, [caseList, assignedLeadsList]);

  // Build the badge map: use reviewCountByCase which now contains role-specific counts
  const badgeCountByCase = useMemo(() => {
    return reviewCountByCase;
  }, [reviewCountByCase]);

  const getCaseBadgeCount = (caseId) =>
    badgeCountByCase[String(caseId)] || 0;

  const handleCaseSelect = (c) => {
    setSelectedCase({ _id: c._id, caseNo: c.caseNo, caseName: c.title, role: c.role });
    const dest = c.role === "Investigator" || c.role === "Officer" ? "/Investigator" : c.role === "Read Only" ? "/LeadsDesk" : "/CasePageManager";
    navigate(dest, { state: { caseDetails: { _id: c._id, caseNo: c.caseNo, caseName: c.title, role: c.role } } });
  };

  // Admin variant
  if (variant === "admin") {
    return (
      <aside className="sidebar">
        <ul className="sidebar-list">
          <li
            className={`sidebar-item ${location.pathname === "/AdminTeam" && !showAddCase ? "active" : ""}`}
            onClick={() => {
              onShowCaseSelector?.(false);
              navigate("/AdminTeam");
            }}
          >
            <img src={folderIcon} className="sidebar-icon" alt="" />
            <span>Case Management</span>
          </li>
          {systemRole !== ROLES.CASE_SPECIFIC && (
            <li
              className={`sidebar-item ${showAddCase ? "active" : ""}`}
              style={{ paddingLeft: 32 }}
              onClick={() => onShowCaseSelector?.(true)}
            >
              <img src={addIcon} className="sidebar-icon" alt="" />
              <span>Add Case</span>
            </li>
          )}
          <li
            className={`sidebar-item ${location.pathname === "/AdminUR" && !showAddCase ? "active" : ""}`}
            onClick={() => { onShowCaseSelector?.(false); navigate("/AdminUR"); }}
          >
            <img src={folderIcon} className="sidebar-icon" alt="" />
            <span>User Registration</span>
          </li>
          <li
            className={`sidebar-item ${location.pathname === "/AdminUserList" && !showAddCase ? "active" : ""}`}
            onClick={() => { onShowCaseSelector?.(false); navigate("/AdminUserList"); }}
          >
            <img src={folderIcon} className="sidebar-icon" alt="" />
            <span>Registered Users</span>
          </li>
          <li
            className={`sidebar-item ${location.pathname === "/AdminAgencies" && !showAddCase ? "active" : ""}`}
            onClick={() => { onShowCaseSelector?.(false); navigate("/AdminAgencies"); }}
          >
            <img src={folderIcon} className="sidebar-icon" alt="" />
            <span>Agency Management</span>
          </li>
        </ul>
      </aside>
    );
  }

  // Home variant (kept minimal)
  if (variant === "home") {
    const showArchivedTab = isDSProp !== null ? isDSProp : isUserDS;
    return (
      <aside className="sidebar">
        <ul className="sidebar-list">
          <li
            className={`sidebar-item ${activeTab === "notifications" ? "active" : ""}`}
            onClick={() => setActiveTab?.("notifications")}
          >
            <img src={bellIcon} className="sidebar-icon" alt="" />
            <span>Notifications</span>
          </li>

          <li
            className={`sidebar-item ${["cases"].includes(activeTab) && !showAddCase ? "active" : ""}`}
            onClick={() => {
              onShowCaseSelector?.(false);
              setActiveTab?.("cases");
            }}
          >
            <img src={folderIcon} className="sidebar-icon" alt="" />
            <span>Case Management</span>
          </li>

          {systemRole !== ROLES.CASE_SPECIFIC && (
            <li
              className={`sidebar-item ${showAddCase ? "active" : ""}`}
              style={{ paddingLeft: 32 }}
              onClick={() => {
                setActiveTab?.("cases");
                onShowCaseSelector?.(true);
              }}
            >
              <img src={addIcon} className="sidebar-icon" alt="" />
              <span>Add Case</span>
            </li>
          )}

          {showArchivedTab && (
            <li
              className={`sidebar-item ${activeTab === "archived" ? "active" : ""}`}
              onClick={() => navigate("/ClosedCase")}
            >
              <img src={folderIcon} className="sidebar-icon" alt="" />
              <span>Closed Cases</span>
            </li>
          )}
        </ul>
      </aside>
    );
  }

  // Officer variant — Home + Case page + Other Open Cases
  if (selectedCase?.role === "Officer") {
    return (
      <aside className="sidebar">
        <ul className="sidebar-list">
          <li
            className={`sidebar-item ${activePage === "HomePage" || activePage === "AdminTeam" ? "active" : ""}`}
            onClick={() =>
              systemRole === "Admin"
                ? navigate("/AdminTeam")
                : navigate("/HomePage", { state: { caseDetails, activeTab: "cases" } })
            }
          >
            <img src={homeIcon} className="sidebar-icon" alt="" />
            <span>PIMS Home</span>
          </li>
          <li
            className={`sidebar-item ${activePage === "Investigator" ? "active" : ""}`}
            onClick={goToCasePage}
          >
            <img src={folderIcon} className="sidebar-icon" alt="" />
            <span>Case: {selectedCase?.caseNo || "-"}</span>
          </li>

          {/* Other Open Cases */}
          <li className="sidebar-item" onClick={() => setCaseDropdownOpen((o) => !o)}>
            <img src={folderIcon} className="sidebar-icon" alt="" />
            <span>Other Open Cases {caseDropdownOpen ? "▲" : "▼"}</span>
          </li>
          {caseDropdownOpen && (
            <ul className="dropdown-list1">
              {caseList
                .filter((c) => String(c._id) !== String(selectedCase?._id || selectedCase?.id))
                .map((c) => {
                  const count = getCaseBadgeCount(c._id);
                  const isActive = String(selectedCase?._id || selectedCase?.id) === String(c._id);
                  return (
                    <li
                      key={String(c._id)}
                      className={`sidebar-item${isActive ? " active" : ""}`}
                      onClick={() => handleCaseSelect(c)}
                    >
                      <div className="case-headerSB">
                        <span>Case: {c.caseNo}</span>
                        <span className="sidebar-number">{count}</span>
                      </div>
                    </li>
                  );
                })}
            </ul>
          )}
        </ul>
      </aside>
    );
  }

  // Default variant
  return (
    <aside className="sidebar">
      <ul className="sidebar-list">
        <li
          className={`sidebar-item ${activePage === "HomePage" || activePage === "AdminCM" || activePage === "AdminTeam" ? "active" : ""}`}
          onClick={() =>
            systemRole === "Admin"
              ? navigate("/AdminTeam")
              : navigate("/HomePage", { state: { caseDetails, activeTab: "cases" } })
          }
        >
          <img src={homeIcon} className="sidebar-icon" alt="" />
          <span>PIMS Home</span>
        </li>

        <li
          className={`sidebar-item ${["CasePageManager", "Investigator"].includes(activePage) ? "active" : ""}`}
          onClick={goToCasePage}
        >
          <img src={folderIcon} className="sidebar-icon" alt="" />
          <span>Case: {selectedCase?.caseNo || "-"}</span>
        </li>

        {["Case Manager", "Detective Supervisor"].includes(selectedCase?.role) && (
          <li
            className={`sidebar-item ${activePage === "CreateLead" ? "active" : ""}`}
            style={{ paddingLeft: 30 }}
            onClick={handleCreateLead}
          >
            <img src={addIcon} className="sidebar-icon" alt="" />
            <span>Add Lead</span>
          </li>
        )}

        {selectedLead && LEAD_CRUMB_PAGES.has(activePage) && (
          <li
            className={`sidebar-item ${LEAD_CRUMB_PAGES.has(activePage) ? "active" : ""}`}
            style={{ paddingLeft: 30 }}
            onClick={() => {
              const dest = activePage === "LeadReview" ? "/LeadReview" : "/LeadInformation";
              navigate(dest, {
                state: { caseDetails: selectedCase, leadDetails: selectedLead },
              });
            }}
          >
            <img src={folderIcon1} className="sidebar-icon" alt="" />
            <span>Lead: {selectedLead?.leadNo || "-"}</span>
          </li>
        )}

        <li
          className={`sidebar-item ${activePage === "CaseInformation" ? "active" : ""}`}
          style={{ paddingLeft: 30 }}
          onClick={() => navigate("/CaseInformation", { state: { caseDetails } })}
        >
          <img src={folderIcon1} className="sidebar-icon" alt="" />
          <span>Case Information</span>
        </li>

        {/* Leads Desk — visible to all roles */}
        <li
          className={`sidebar-item ${activePage === "LeadsDesk" ? "active" : ""}`}
          style={{ paddingLeft: 30, display: "flex", alignItems: "center", justifyContent: "space-between" }}
          onClick={() => navigate("/LeadsDesk", { state: { caseDetails } })}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img src={folderIcon1} className="sidebar-icon" alt="" />
            <span>Leads Desk</span>
          </div>
        </li>

        <li
          className={`sidebar-item ${activePage === "GenerateReport" ? "active" : ""}`}
          style={{ paddingLeft: 30 }}
          onClick={() => navigate("/GenerateReport", { state: { caseDetails } })}
        >
          <img src={printIcon} className="sidebar-icon" alt="" />
          <span>Generate Report</span>
        </li>

        <li
          className={`sidebar-item ${activePage === "LeadLog" ? "active" : ""}`}
          style={{ paddingLeft: 30 }}
          onClick={() => navigate("/LeadLog", { state: { caseDetails } })}
        >
          <img src={logIcon} className="sidebar-icon" alt="" />
          <span>Case Log</span>
        </li>

        {selectedCase?.role !== "Read Only" && (
          <li
            className={`sidebar-item ${activePage === "SearchLead" ? "active" : ""}`}
            onClick={() => navigate("/SearchLead", { state: { caseDetails } })}
          >
            <img src={searchIcon} className="sidebar-icon" alt="" />
            <span>Advanced Search </span>
          </li>
        )}

        {/* Other Open Cases */}
        {systemRole !== "Admin" && (
          <li className="sidebar-item" onClick={() => setCaseDropdownOpen((o) => !o)}>
            <img src={folderIcon} className="sidebar-icon" alt="" />
            <span>Other Open Cases {caseDropdownOpen ? "▲" : "▼"}</span>
          </li>
        )}

        {systemRole !== "Admin" && caseDropdownOpen && (
          <ul className="dropdown-list1">
            {caseList
              .filter((c) => String(c._id) !== String(selectedCase?._id || selectedCase?.id))
              .map((c) => {
                const count = getCaseBadgeCount(c._id);
                const isActive = String(selectedCase?._id || selectedCase?.id) === String(c._id);
                return (
                  <li
                    key={String(c._id)}
                    className={`sidebar-item${isActive ? " active" : ""}`}
                    onClick={() => handleCaseSelect(c)}
                  >
                    <div className="case-headerSB">
                      <span>Case: {c.caseNo}</span>
                      <span className="sidebar-number">{count}</span>
                    </div>
                  </li>
                );
              })}
          </ul>
        )}

        {isUserDS && (
          <li className="sidebar-item" onClick={() => navigate("/ClosedCase")}>
            <img src={folderIcon} className="sidebar-icon" alt="" />
            <span>Closed Cases</span>
          </li>
        )}

        {/* {["Case Manager", "Detective Supervisor", "Investigator"].includes(selectedCase?.role) && (
          <li
            className={`sidebar-item ${activePage === "Chatbot" ? "active" : ""}`}
            onClick={() => navigate("/Chatbot", { state: { caseDetails } })}
          >
            <img src={folderIcon} className="sidebar-icon" alt="" />
            <span>Chatbot</span>
          </li>
        )}
         {["Case Manager", "Detective Supervisor", "Investigator"].includes(selectedCase?.role) && (
          <li
            className={`sidebar-item ${activePage === "AdminDashboard" ? "active" : ""}`}
            onClick={() => navigate("/AdminDashboard", { state: { caseDetails } })}
          >
            <img src={folderIcon} className="sidebar-icon" alt="" />
            <span>Crime Dashboard</span>
          </li>
        )}
         {["Case Manager", "Detective Supervisor", "Investigator"].includes(selectedCase?.role) && (
          <li
            className={`sidebar-item ${activePage === "ViewTimeline" ? "active" : ""}`}
            onClick={() => navigate("/ViewTimeline", { state: { caseDetails } })}
          >
            <img src={folderIcon} className="sidebar-icon" alt="" />
            <span>Timeline Enteries</span>
          </li>
        )} */}
      </ul>
    </aside>
  );
};
